import React from "react";

import {
  AdminNavIcon,
  CRMRetargetingIcon,
  ContextualABMIcon,
  ContextualMediaBuyingIcon,
  ContextualPersonaIcon,
  CustomCampaignsIcon,
  GeoTargetingIcon,
  GeographiesIcon,
  HomeNavIcon,
  ManageAdvertisersIcon,
  PersonaRetargetingIcon,
  PlaysNavIcon,
  PublicationListsIcon,
  RolesTitlesIcon,
  SiteRetargetingIcon,
  TraditionalABMIcon,
  UploadAdGroupsIcon,
} from "./AppSidebar.icons";

export interface SubItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  tooltip?: { title: string; text: string };
}

export interface NavItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  placement?: "top" | "bottom";
  hasArrow?: boolean;
  sectionPaths?: string[];
  usesSelectedAdvertiser?: boolean;
  subItems?: SubItem[];
}

export const NAV_ITEMS: NavItem[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: <HomeNavIcon />,
    hasArrow: false,
    usesSelectedAdvertiser: true,
    sectionPaths: ["/advertisers"],
  },
  {
    key: "launch-campaign",
    label: "Launch Campaign",
    icon: <PlaysNavIcon />,
    hasArrow: true,
    sectionPaths: [
      "/campaigns/create/custom",
      "/campaigns/create/traditional-abm",
      "/campaigns/create/contextual-abm",
      "/campaigns/create/contextual-media-buying",
      "/campaigns/create/contextual-persona",
      "/campaigns/create/geo-targeting",
      "/campaigns/create/crm-retargeting",
      "/campaigns/create/site-retargeting",
      "/campaigns/create/persona-retargeting",
    ],
    subItems: [
      {
        label: "Traditional ABA",
        path: "/campaigns/create/traditional-abm",
        icon: <TraditionalABMIcon />,
        tooltip: {
          title: "Traditional Account-Based Advertising",
          text: "Target high-value account decision makers by Persona roles and titles. Select Target Accounts from HubSpot, connect audiences to HubSpot Company Segments (Lists) or upload CSVs of company domains.",
        },
      },
      {
        label: "Contextual ABA",
        path: "/campaigns/create/contextual-abm",
        icon: <ContextualABMIcon />,
        tooltip: {
          title: "Contextual Account-Based Advertising",
          text: "Execute Traditional or Dynamic Account-Based Advertising using Context vs Role and Title to reach audiences in high-value environments.",
        },
      },
      {
        label: "Contextual Media Buying",
        path: "/campaigns/create/contextual-media-buying",
        icon: <ContextualMediaBuyingIcon />,
        tooltip: {
          title: "Contextual Media Buying",
          text: "Reach audiences in high-value contextual environments like business and trade publications with no minimum spend, low CPMs and no sales process.",
        },
      },
      {
        label: "Contextual / Persona Targeting",
        path: "/campaigns/create/contextual-persona",
        icon: <ContextualPersonaIcon />,
        tooltip: {
          title: "Contextual / Persona Targeting",
          text: "Overlay Persona roles and titles on top of context to reach precise audiences in context.",
        },
      },
      {
        label: "Geo-Targeting",
        path: "/campaigns/create/geo-targeting",
        icon: <GeoTargetingIcon />,
        tooltip: {
          title: "Geo-Targeting",
          text: "Target specific Zip Codes/Postal Codes or States.\nIdeal for regional Services Businesses.\nReach local and regional publications like print, tv and radio news sites.",
        },
      },
      {
        label: "CRM Retargeting",
        path: "/campaigns/create/crm-retargeting",
        icon: <CRMRetargetingIcon />,
        tooltip: {
          title: "CRM Retargeting",
          text: "Reach decision-makers across the funnel by turning emails into open-web and CTV audiences.\nSync HubSpot lists or upload CSVs to keep audiences current.\nResolve email audiences with high reach and accuracy.\nSupport prospecting without opt-ins.\nEngage or re-engage key audiences with built-in sales and marketing alignment.",
        },
      },
      {
        label: "Visitor Retargeting",
        path: "/campaigns/create/site-retargeting",
        icon: <SiteRetargetingIcon />,
        tooltip: {
          title: "Visitor Retargeting",
          text: "Retargeting outperforms by prioritizing premium SSP networks and high-quality sites.\nTarget site visitors by page or page group to align campaigns with audience and buyer stages.",
        },
      },
      {
        label: "Visitor Persona Retargeting",
        path: "/campaigns/create/persona-retargeting",
        icon: <PersonaRetargetingIcon />,
        tooltip: {
          title: "Visitor Persona Retargeting",
          text: "Segment website visitors by separating into buyer persona audiences for precise message targeting, improving engagement.",
        },
      },
      {
        label: "Custom Campaigns",
        path: "/campaigns/create/custom",
        icon: <CustomCampaignsIcon />,
        tooltip: {
          title: "Custom Campaigns",
          text: "Take control of your audience creation by selecting your own mix of targeting attributes from our available options.\nThe option selections are 'And', so each selection narrows your audience by that attribute.",
        },
      },
    ],
  },
  {
    key: "data-configuration",
    label: "Data & Configuration",
    icon: <AdminNavIcon />,
    hasArrow: true,
    sectionPaths: [
      "/publication-lists",
      "/visitors-retargeting",
      "/ads-upload",
      "/zip-codes",
      "/roles-titles",
      "/state-province",
      "/target-companies",
      "/create-campaign-template",
      "/campaigns/create/from-template",
      "/terms-of-use",
      "/privacy-policy",
    ],
    subItems: [
      {
        label: "Set-up Publication Lists",
        path: "/publication-lists",
        icon: <PublicationListsIcon />,
        tooltip: {
          title: "Set-up Publication Lists",
          text: "Organize your contextual advertising by using curated publications or curate your own.",
        },
      },
      { label: "Visitors Retargeting Segments", path: "/visitors-retargeting", icon: <SiteRetargetingIcon /> },
      {
        label: "Upload Ad Groups",
        path: "/ads-upload",
        icon: <UploadAdGroupsIcon />,
        tooltip: {
          title: "Upload Ad Groups",
          text: "Manage your advertising creative here.",
        },
      },
      { label: "Set-up Geographies", path: "/zip-codes", icon: <GeographiesIcon /> },
      {
        label: "Set-up Roles & Titles",
        path: "/roles-titles",
        icon: <RolesTitlesIcon />,
        tooltip: {
          title: "Set-up Roles and Titles",
          text: "Create reusable groups of role and title targeting options for campaign creation.",
        },
      },
      {
        label: "Set-up States & Provinces",
        path: "/state-province",
        icon: <GeoTargetingIcon />,
        tooltip: {
          title: "Set-up States & Provinces",
          text: "Create reusable groups of state and province targeting options for campaign creation.",
        },
      },
      { label: "Company Match Rates", path: "/target-companies", icon: <ManageAdvertisersIcon /> },
    ],
  },
];
